import { format, isValid, parse } from 'date-fns';

// Single display format across the platform: "04 Feb 2025"
const DISPLAY_DATE_LOCALE = 'en-GB';
const DISPLAY_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
};

// Утилиты для работы с датами
export const dateUtils = {
  /** ISO yyyy-MM-dd → dd/MM/yyyy (calendar date in local zone; do not use parseISO — UTC shifts the day) */
  formatIsoDateToDdMmYyyy: (iso: string): string => {
    const s = iso.trim();
    if (!s || !/^\d{4}-\d{2}-\d{2}/.test(s)) return '';
    const y = parseInt(s.slice(0, 4), 10);
    const m = parseInt(s.slice(5, 7), 10);
    const day = parseInt(s.slice(8, 10), 10);
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(day)) return '';
    const d = new Date(y, m - 1, day);
    if (Number.isNaN(d.getTime())) return '';
    return format(d, 'dd/MM/yyyy');
  },

  /** dd/MM/yyyy → ISO yyyy-MM-dd; null if empty; invalid input returns null */
  parseDdMmYyyyToIso: (text: string): string | null => {
    const t = text.trim();
    if (!t) return null;
    const d = parse(t, 'dd/MM/yyyy', new Date());
    if (!isValid(d)) return null;
    return format(d, 'yyyy-MM-dd');
  },

  /**
   * Input mask: keep digits only (max 8), insert / after day and month.
   * "20042026" → "20/04/2026"; paste "20/04/2026" works the same.
   */
  formatInputAsDdMmYyyy: (value: string): string => {
    const only = value.replace(/\D/g, '').slice(0, 8);
    if (only.length <= 2) return only;
    if (only.length <= 4) return `${only.slice(0, 2)}/${only.slice(2)}`;
    return `${only.slice(0, 2)}/${only.slice(2, 4)}/${only.slice(4)}`;
  },

  /** Canonical display format for dates (dd MMM yyyy) — use everywhere in UI */
  formatDisplayDate: (date: string | Date): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(d.getTime())) return String(date);
    return d.toLocaleDateString(DISPLAY_DATE_LOCALE, DISPLAY_DATE_OPTIONS);
  },

  /** Format a date range; if start and end are the same calendar day (or end is midnight next day), show that day for both (e.g. "26 Feb 2026 – 26 Feb 2026") */
  formatDisplayDateRange: (start: string | Date, end: string | Date): string => {
    const s = typeof start === 'string' ? new Date(start) : start;
    const e = typeof end === 'string' ? new Date(end) : end;
    if (Number.isNaN(s.getTime())) return String(start);
    if (Number.isNaN(e.getTime())) return String(end);
    const toYmd = (d: Date) =>
      `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    const startYmd = toYmd(s);
    const endYmd = toYmd(e);
    const endIsMidnight =
      e.getUTCHours() === 0 && e.getUTCMinutes() === 0 && e.getUTCSeconds() === 0;
    const nextDay = new Date(s.getTime());
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    const sameDay = startYmd === endYmd || (endIsMidnight && toYmd(nextDay) === endYmd);
    const endDisplay = sameDay ? start : end;
    return `${dateUtils.formatDisplayDate(start)} – ${dateUtils.formatDisplayDate(endDisplay)}`;
  },

  formatDate: (date: string | Date): string => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  },

  formatDateTime: (date: string | Date): string => {
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  },

  getDateRange: (filter: string): { start: string; end: string } => {
    const today = new Date();

    switch (filter) {
      case 'today':
        const todayStr = today.toISOString().split('T')[0];
        return { start: todayStr, end: todayStr };

      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];
        return { start: yesterdayStr, end: yesterdayStr };

      case 'thisWeek':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return {
          start: startOfWeek.toISOString().split('T')[0],
          end: endOfWeek.toISOString().split('T')[0],
        };

      case 'lastWeek':
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        return {
          start: lastWeekStart.toISOString().split('T')[0],
          end: lastWeekEnd.toISOString().split('T')[0],
        };

      case 'thisMonth':
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();
        const start = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(currentYear, currentMonth + 1, 0).getDate();
        const end = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        return { start, end };

      case 'lastMonth':
        const prevYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
        const prevMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
        const prevStart = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-01`;
        const prevLastDay = new Date(prevYear, prevMonth + 1, 0).getDate();
        const prevEnd = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(prevLastDay).padStart(2, '0')}`;
        return { start: prevStart, end: prevEnd };

      default:
        return { start: '', end: '' };
    }
  },

  addTimeToEndDate: (endDate: string): string => {
    return endDate + 'T23:59:59.999Z';
  },
};

// Утилиты для работы с данными
export const dataUtils = {
  calculateTotalTime: (entries: any[]): number => {
    return entries.reduce((sum, entry) => sum + (entry.timeSpent || 0), 0);
  },

  groupByAuthor: (entries: any[]): Record<string, any[]> => {
    return entries.reduce((groups, entry) => {
      const author = entry.author;
      if (!groups[author]) {
        groups[author] = [];
      }
      groups[author].push(entry);
      return groups;
    }, {});
  },

  groupByDate: (entries: any[]): Record<string, any[]> => {
    return entries.reduce((groups, entry) => {
      const date = entry.startDate.split('T')[0];
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(entry);
      return groups;
    }, {});
  },

  sortByDate: (entries: any[], ascending = false): any[] => {
    return [...entries].sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return ascending ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
    });
  },
};

// Утилиты для валидации
export const validationUtils = {
  isValidEmail: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  isValidDate: (date: string): boolean => {
    const d = new Date(date);
    return d instanceof Date && !isNaN(d.getTime());
  },

  isValidDateRange: (startDate: string, endDate: string): boolean => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return start <= end;
  },
};

// Утилиты для форматирования
export const formatUtils = {
  formatTime: (hours: number): string => {
    return hours.toFixed(1) + ' hrs';
  },

  formatNumber: (num: number, decimals = 1): string => {
    return num.toFixed(decimals);
  },

  truncateText: (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  },
};

// Экспортируем утилиты для нормализации имён
export * from './nameNormalizer';
export * from './productDisplayLabel';
